import { useToast } from '@chakra-ui/react';
import { showToast } from '@diamond/shared/ui';
import { VoucherFormSchema } from '@diamond/shared/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { defaultValues } from '../constants';
import type { CreateVoucherPayload, CreateVoucherRequest } from '../types';
import {
  buildCreateVoucherPayload,
  clearDraftFromSessionStorage,
  getStepIndexFromUrl,
  getUrlFromStepIndex,
  hasFormChanges,
  loadDraftFromSessionStorage,
  saveDraftToSessionStorage,
  stepFieldMap,
  stepUrls,
} from '../utils';
import { useCreateVoucherMutation } from './api-hooks';

const FINAL_STEP_INDEX = stepUrls.length - 1;

export const useVoucherCreate = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentStep = useMemo(() => {
    return Math.min(
      Math.max(getStepIndexFromUrl(searchParams.get('step')), 0),
      FINAL_STEP_INDEX
    );
  }, [searchParams]);

  const draftData = useMemo(() => loadDraftFromSessionStorage(), []);
  const initialValues = draftData?.formData || defaultValues;

  const form = useForm<CreateVoucherRequest>({
    defaultValues: initialValues,
    resolver: yupResolver(VoucherFormSchema),
    mode: 'onBlur',
  });

  const createVoucherMutation = useCreateVoucherMutation({
    onSuccess: () => {
      clearDraftFromSessionStorage();
      showToast(toast, 'success', 'Voucher berhasil dibuat');
      navigate('/vouchers');
    },
    onError: (
      error: Error & {
        response?: { data?: { message?: string; error?: string } };
      }
    ) => {
      // Extract error message from API response
      const apiMessage =
        error.response?.data?.message || error.response?.data?.error;
      const errorMessage = apiMessage || error.message;
      showToast(toast, 'error', `Gagal membuat voucher: ${errorMessage}`);
    },
  });

  // Trigger validation for current step when draft is loaded or step changes
  useEffect(() => {
    const hasDraft = !!draftData;
    if (hasDraft) {
      const fields = stepFieldMap[currentStep];
      if (fields && fields.length > 0) {
        // Use setTimeout to ensure form is fully initialized and all hooks have run
        const timeoutId = setTimeout(() => {
          void form.trigger(fields);
        }, 150);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [currentStep, form, draftData]);

  useEffect(() => {
    const subscription = form.watch((formData) => {
      if (hasFormChanges(formData, defaultValues)) {
        saveDraftToSessionStorage(
          formData as CreateVoucherRequest,
          currentStep
        );
      }
    });

    return () => subscription.unsubscribe();
  }, [form, currentStep]);

  const updateStepInUrl = useCallback(
    (stepIndex: number) => {
      const newParams = new URLSearchParams(searchParams);
      const stepUrl = getUrlFromStepIndex(stepIndex);

      if (stepIndex === 0 || !stepUrl) {
        newParams.delete('step');
      } else {
        newParams.set('step', stepUrl);
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Track if this is the initial mount to handle URL-based step access
  const isInitialMountRef = useRef(true);

  // Redirect to step 0 if no draft data exists and user tries to access step > 0 via URL
  // This handles the case when session storage is cleared (e.g., tab closed) but user
  // navigates directly to a later step via URL
  // Only runs on initial mount, not when user navigates between steps
  useEffect(() => {
    if (!isInitialMountRef.current) return;
    isInitialMountRef.current = false;

    const hasDraft = Boolean(draftData);

    if (!hasDraft && currentStep > 0) {
      updateStepInUrl(0);
    }
  }, [currentStep, draftData, updateStepInUrl]);

  const submitForm = useCallback(
    async (formData: CreateVoucherRequest) => {
      const payload: CreateVoucherPayload = buildCreateVoucherPayload(formData);

      createVoucherMutation.mutate(payload);
    },
    [createVoucherMutation]
  );

  const handleInvalidSubmit = useCallback(() => {
    showToast(
      toast,
      'error',
      'Mohon lengkapi semua field yang wajib diisi sebelum membuat voucher'
    );
  }, [toast]);

  const handleNextStep = useCallback(async () => {
    const fields = stepFieldMap[currentStep];

    if (fields && fields.length) {
      const isStepValid = await form.trigger(fields, {
        shouldFocus: true,
      });

      if (!isStepValid) {
        showToast(
          toast,
          'error',
          'Mohon lengkapi semua field yang wajib diisi sebelum melanjutkan'
        );
        return;
      }
    }

    if (currentStep === 0 && !form.getValues('codeValidated')) {
      showToast(toast, 'error', 'Silakan cek kode voucher terlebih dahulu');
      return;
    }

    if (currentStep >= FINAL_STEP_INDEX) {
      await form.handleSubmit(submitForm, handleInvalidSubmit)();
      return;
    }

    updateStepInUrl(currentStep + 1);
  }, [
    currentStep,
    form,
    handleInvalidSubmit,
    submitForm,
    toast,
    updateStepInUrl,
  ]);

  const handlePreviousStep = useCallback(() => {
    if (currentStep === 0) {
      return;
    }

    updateStepInUrl(currentStep - 1);
  }, [currentStep, updateStepInUrl]);

  const handleStepChange = useCallback(
    async (stepIndex: number) => {
      const fields = stepFieldMap[currentStep];
      const isMovingForward = stepIndex > currentStep;

      if (fields && fields.length && isMovingForward) {
        const isStepValid = await form.trigger(fields, {
          shouldFocus: true,
        });

        if (!isStepValid) {
          showToast(
            toast,
            'error',
            'Mohon lengkapi form pada step ini sebelum melanjutkan'
          );
          return;
        }
      }

      if (
        currentStep === 0 &&
        isMovingForward &&
        !form.getValues('codeValidated')
      ) {
        showToast(toast, 'error', 'Silakan cek kode voucher terlebih dahulu');
        return;
      }

      updateStepInUrl(stepIndex);
    },
    [currentStep, form, toast, updateStepInUrl]
  );

  return {
    form,
    currentStep,
    finalStepIndex: FINAL_STEP_INDEX,
    handleNextStep,
    handlePreviousStep,
    handleStepChange,
    isSubmitting: createVoucherMutation.isPending,
  };
};
